import { ProcedureStatus, reschedulableProcedureStatuses } from '@caresend/types';
import { arrayToObj, nullishFilter, formatFullName, getFedExTrackingURL, hasStatus, getFastingRequired } from '@caresend/utils';
import { computed } from 'vue';
import { getStore } from '../../../../store/helpers/initStore.js';

/**
 * Shared logic for DraftWaypointOverviewCard, DraftWaypointBookingInfo,
 * and DraftWaypointProcedureInfo, and for local patient procedure data
 * displayed after scheduling.
 *
 * Currently `passedData` is used for the facility visit page and relies only
 * on data sourced from the pinia orders store after fetching a denormalized
 * order (as patient does not have access to orders, procedures, etc). These
 * components are also used in the scheduling flow without `passedData`. If
 * adding new items, ensure that the data is available in both cases.
 */
const useDraftWaypointOverviewCardData = (props) => {
    const store = getStore();
    const bookingIDs = computed(() => {
        var _a, _b;
        if (props.passedData)
            return Object.keys((_b = (_a = props.passedData) === null || _a === void 0 ? void 0 : _a.bookingInfos) !== null && _b !== void 0 ? _b : {});
        return Object.values(store.getters['schedulingFlow/getBookingsData'])
            .map((booking) => booking.id);
    });
    const getProcedureIDs = (bookingID) => {
        var _a, _b, _c, _d;
        if (props.passedData) {
            return Object.keys((_b = (_a = props.passedData.bookingInfos[bookingID]) === null || _a === void 0 ? void 0 : _a.procedureInfos) !== null && _b !== void 0 ? _b : {});
        }
        const bookingsData = store.getters['schedulingFlow/getBookingsData'];
        const booking = bookingsData[bookingID];
        const bookingProcedures = Object.keys((_c = booking === null || booking === void 0 ? void 0 : booking.procedures) !== null && _c !== void 0 ? _c : {});
        const { draftWaypoint } = store.state.schedulingFlow;
        const draftWaypointProcedureIDs = Object.keys((_d = draftWaypoint === null || draftWaypoint === void 0 ? void 0 : draftWaypoint.procedures) !== null && _d !== void 0 ? _d : {});
        const filteredProcedures = bookingProcedures.filter((procedureID) => {
            var _a;
            const procedureIncludedOnDraftWaypoint = draftWaypointProcedureIDs.includes(procedureID);
            const procedureCanceled = hasStatus((_a = store.getters['schedulingFlow/getProcedures'][procedureID]) === null || _a === void 0 ? void 0 : _a.status, ProcedureStatus.CANCELED);
            // The procedure can only be canceled if it is specifically being rescheduled for.
            // Since we are going to directly set the procedureIDs
            const canceledProcedureBeingRescheduled = procedureCanceled ? store.getters['schedulingFlow/getIsCanceledProcedureBeingRescheduled'](procedureID) : false;
            const procedureEligible = procedureIncludedOnDraftWaypoint
                && (!procedureCanceled || canceledProcedureBeingRescheduled);
            return procedureEligible;
        });
        return filteredProcedures;
    };
    const getProcedureInfo = (bookingID, procedureID) => {
        var _a, _b, _c;
        if (props.passedData) {
            return (_b = (_a = props.passedData.bookingInfos[bookingID]) === null || _a === void 0 ? void 0 : _a.procedureInfos) === null || _b === void 0 ? void 0 : _b[procedureID];
        }
        const procedure = store.getters['schedulingFlow/getProcedures'][procedureID];
        if (!procedure)
            return undefined;
        const requiresFasting = getFastingRequired(procedure);
        const info = {
            ...procedure,
            placeAccounts: procedure.placeAccounts,
            dropOffPlaceGroups: procedure.dropOffPlaceGroups,
            productIDs: Object.keys((_c = procedure.products) !== null && _c !== void 0 ? _c : {}),
            status: procedure.status,
            ...(requiresFasting && { requiresFasting }),
        };
        return info;
    };
    const getBookingInfo = (bookingID) => {
        var _a;
        if (props.passedData)
            return (_a = props.passedData.bookingInfos) === null || _a === void 0 ? void 0 : _a[bookingID];
        const bookingsData = store.getters['schedulingFlow/getBookingsData'];
        const booking = bookingsData[bookingID];
        const patients = store.getters['schedulingFlow/getPatients'];
        const patient = (booking === null || booking === void 0 ? void 0 : booking.patientID) ? patients[booking.patientID] : undefined;
        const procedureIDs = getProcedureIDs(bookingID);
        const procedureInfos = arrayToObj(procedureIDs.map((id) => getProcedureInfo(bookingID, id))
            .filter(nullishFilter), 'id');
        const info = {
            id: bookingID,
            patientID: booking === null || booking === void 0 ? void 0 : booking.patientID,
            patientName: formatFullName(patient),
            patientShortName: formatFullName(patient, false, false, 'Unknown patient', true),
            procedureInfos,
            patientShipmentTrackingNumber: booking === null || booking === void 0 ? void 0 : booking.patientShipmentTrackingNumber,
            sampleShipmentTrackingNumber: booking === null || booking === void 0 ? void 0 : booking.sampleShipmentTrackingNumber,
        };
        return info;
    };
    const structuredData = computed(() => {
        if (props.passedData)
            return props.passedData;
        const bookingInfos = arrayToObj(bookingIDs.value.map(getBookingInfo).filter(nullishFilter), 'id');
        return { bookingInfos };
    });
    const getShipmentTracking = () => {
        const { bookingInfos } = structuredData.value;
        return Object.values(bookingInfos).reduce((acc, bookingInfo) => {
            const { id: bookingID, patientShipmentTrackingNumber, sampleShipmentTrackingNumber, } = bookingInfo;
            if (patientShipmentTrackingNumber) {
                if (!acc.patientTrackingURLs)
                    acc.patientTrackingURLs = {};
                acc.patientTrackingURLs[bookingID] = getFedExTrackingURL(patientShipmentTrackingNumber);
            }
            if (sampleShipmentTrackingNumber) {
                if (!acc.sampleTrackingURLs)
                    acc.sampleTrackingURLs = {};
                acc.sampleTrackingURLs[bookingID] = getFedExTrackingURL(sampleShipmentTrackingNumber);
            }
            return acc;
        }, {});
    };
    // LOCAL PATIENT PROCEDURE DATA
    const getLocalProcedurePlaceGroupProductNames = (procedure) => {
        var _a, _b;
        const firstDropOffPlaceGroupID = Object.keys((_a = procedure.dropOffPlaceGroups) !== null && _a !== void 0 ? _a : {})[0];
        if (!firstDropOffPlaceGroupID)
            return [];
        return (_b = procedure.productIDs) === null || _b === void 0 ? void 0 : _b.map((productID) => {
            var _a, _b;
            const product = store.getters['variables/getProductByID'](productID);
            return (_b = (_a = product === null || product === void 0 ? void 0 : product.placeGroups) === null || _a === void 0 ? void 0 : _a[firstDropOffPlaceGroupID]) === null || _b === void 0 ? void 0 : _b.friendlyName;
        }).filter(nullishFilter);
    };
    /**
     * Used to show information on the visit list/page while algolia updates.
     * Only scheduling flow data supported, not passed data, as this will only
     * be used at the end of the scheduling flow.
     *
     * We also convert the status of the procedures to CREATED if they are
     * in the reschedulableProcedureStatuses array.
     */
    const localPatientProcedureData = computed(() => {
        if (!props.draftWaypointID)
            return undefined;
        const { bookingInfos } = structuredData.value;
        const denormalizedProcedureInfos = Object.values(bookingInfos).reduce((denormProcedureInfos, bookingInfo) => {
            var _a;
            const proceduresToAdd = Object.values((_a = bookingInfo.procedureInfos) !== null && _a !== void 0 ? _a : {}).map((procedure) => {
                var _a, _b, _c;
                const existingStatus = (_a = procedure.status) !== null && _a !== void 0 ? _a : ProcedureStatus.CREATED;
                const status = reschedulableProcedureStatuses.includes(existingStatus)
                    ? ProcedureStatus.CREATED
                    : existingStatus;
                return ({
                    bookingID: bookingInfo.id,
                    id: procedure.id,
                    disallowRescheduling: false,
                    patientID: (_b = bookingInfo.patientID) !== null && _b !== void 0 ? _b : '',
                    patientName: bookingInfo.patientName,
                    patientShortName: bookingInfo.patientShortName,
                    productIDs: procedure.productIDs,
                    requiresFasting: procedure.requiresFasting,
                    status,
                    taskID: (_c = procedure.taskID) !== null && _c !== void 0 ? _c : '',
                    testNames: getLocalProcedurePlaceGroupProductNames(procedure),
                });
            });
            return { ...denormProcedureInfos, ...arrayToObj(proceduresToAdd, 'id') };
        }, {});
        const data = {
            id: props.draftWaypointID,
            procedures: denormalizedProcedureInfos,
            shipmentTracking: getShipmentTracking(),
        };
        return data;
    });
    // DRAFT WAYPOINT OVERVIEW CARD
    /** Example: `Lab draw, Urine collection for Sally H, Lab draw for Tim S`
     *
     * If `isExactTheme` is true, the task name will be overridden to `Blood draw`
     * and the patient name will be added to the end of the string.
     * TODO: See if this is the best way to handle this.
     */
    const patientProcedureString = computed(() => {
        const { bookingInfos } = structuredData.value;
        const bookingStrings = Object.values(bookingInfos).map((bookingInfo) => {
            const { patientShortName, procedureInfos } = bookingInfo;
            const procedureNames = Object.values(procedureInfos !== null && procedureInfos !== void 0 ? procedureInfos : {})
                .map((info) => {
                const task = store.getters['variables/getTaskByID'](info.taskID);
                return task === null || task === void 0 ? void 0 : task.info.friendlyName;
            });
            return `${procedureNames.join(', ')} for ${patientShortName}.`;
        });
        return bookingStrings.join(', ');
    });
    // DRAFT WAYPOINT BOOKING INFO
    const patientShortName = computed(() => {
        var _a, _b;
        if (!props.bookingID)
            return 'Unknown patient';
        return (_b = (_a = structuredData.value.bookingInfos[props.bookingID]) === null || _a === void 0 ? void 0 : _a.patientShortName) !== null && _b !== void 0 ? _b : 'Unknown patient';
    });
    const procedureIDs = computed(() => {
        var _a;
        if (!props.bookingID)
            return [];
        const bookingInfo = structuredData.value.bookingInfos[props.bookingID];
        return Object.keys((_a = bookingInfo === null || bookingInfo === void 0 ? void 0 : bookingInfo.procedureInfos) !== null && _a !== void 0 ? _a : {});
    });
    // DRAFT WAYPOINT PROCEDURE INFO
    const procedureInfo = computed(() => {
        var _a, _b;
        if (!props.bookingID || !props.procedureID)
            return undefined;
        return (_b = (_a = structuredData.value.bookingInfos[props.bookingID]) === null || _a === void 0 ? void 0 : _a.procedureInfos) === null || _b === void 0 ? void 0 : _b[props.procedureID];
    });
    const placeGroupID = computed(() => {
        var _a, _b;
        /** Passed data currently only used in the context of facility visits */
        if (props.passedData)
            return (_a = props.passedData.drawPlaceGroupID) !== null && _a !== void 0 ? _a : null;
        if (!procedureInfo.value)
            return null;
        // Use the same logic as the backend: get the first place group from dropOffPlaceGroups
        const firstDropOffPlaceGroupID = Object.keys((_b = procedureInfo.value.dropOffPlaceGroups) !== null && _b !== void 0 ? _b : {})[0];
        return firstDropOffPlaceGroupID !== null && firstDropOffPlaceGroupID !== void 0 ? firstDropOffPlaceGroupID : null;
    });
    const placeGroupName = computed(() => {
        var _a, _b;
        if (props.passedData)
            return props.passedData.drawPlaceGroupName;
        if (!placeGroupID.value)
            return undefined;
        const name = (_b = (_a = store.state.schedulingFlow.placeGroups) === null || _a === void 0 ? void 0 : _a[placeGroupID.value]) === null || _b === void 0 ? void 0 : _b.name;
        return name;
    });
    const testNames = computed(() => {
        var _a, _b, _c;
        return (_c = (_b = (_a = procedureInfo.value) === null || _a === void 0 ? void 0 : _a.productIDs) === null || _b === void 0 ? void 0 : _b.map((id) => {
            var _a, _b;
            const product = store.getters['variables/getProductByID'](id);
            if (!placeGroupID.value)
                return 'Unknown test';
            const name = (_b = (_a = product === null || product === void 0 ? void 0 : product.placeGroups) === null || _a === void 0 ? void 0 : _a[placeGroupID.value]) === null || _b === void 0 ? void 0 : _b.friendlyName;
            // TODO: Consider implementing fallback. The product types in the db do
            // not seem to match our types. Correct the types and determine the
            // appropriate fallback. Possible options:
            // Product.name / Product.info?.name / Product.info?.friendlyName
            return name !== null && name !== void 0 ? name : 'Unknown test';
        })) !== null && _c !== void 0 ? _c : [];
    });
    return {
        bookingIDs,
        localPatientProcedureData,
        patientShortName,
        patientProcedureString,
        placeGroupName,
        procedureIDs,
        procedureInfo,
        structuredData,
        testNames,
    };
};

export { useDraftWaypointOverviewCardData };
